// batch-62 taken-ux (FO-464) — de pure regels rond "een taak aanmaken en
// gereedmelden zonder uitleg". Gedeeld door de API (ongedaan-venster, vorige
// status, meldingstekst) en beide UI's (bevestigingsvraag bij open checklist,
// vervolgbericht, deadline-kleur, standaard-deadline uit een sjabloon), zodat
// de knop nooit iets belooft wat de server anders beslist. Geen I/O; waar tijd
// meetelt, geeft de aanroeper `nu` mee (default new Date()).

import {
  GEREEDMELDING_ONGEDAAN_VENSTER_UUR,
  type OperationeleTaakStatus,
} from '@streep/types'
import { formatDagDatum, parseDagDatum } from './dagtaken-datum'

const UUR_MS = 60 * 60 * 1000

function alsDatum(waarde: Date | string): Date {
  return waarde instanceof Date ? waarde : new Date(waarde)
}

// ─── Ongedaan maken (binnen 24 uur) ───────────────────────────────────────────

/** Tot wanneer een gereedmelding met één klik ongedaan te maken is. */
export function gereedmeldingOngedaanTot(uitgevoerdOp: Date | string): Date {
  return new Date(alsDatum(uitgevoerdOp).getTime() + GEREEDMELDING_ONGEDAAN_VENSTER_UUR * UUR_MS)
}

/**
 * Valt `nu` binnen het ongedaan-venster van deze gereedmelding? Een ontbrekend
 * of onleesbaar tijdstip telt als "nee" (geen stille heropening).
 */
export function binnenOngedaanVenster(
  uitgevoerdOp: Date | string | null | undefined,
  nu: Date = new Date(),
): boolean {
  if (!uitgevoerdOp) return false
  const op = alsDatum(uitgevoerdOp)
  if (Number.isNaN(op.getTime())) return false
  return nu.getTime() <= gereedmeldingOngedaanTot(op).getTime()
}

/** Statussen waarnaar een ongedaan gemaakte gereedmelding kan terugkeren. */
const TERUGKEER_STATUSSEN: ReadonlySet<OperationeleTaakStatus> = new Set<OperationeleTaakStatus>([
  'BACKLOG',
  'GEPLAND',
  'ON_HOLD',
  'HERPLANNEN',
  'NIET_UITGEVOERD',
])

/** De toestand van vóór het gereedmelden, zoals de gereed-audit die vastlegde. */
export interface VorigeTaakToestand {
  status: OperationeleTaakStatus
  statusReden: string | null
  /** ISO-timestamp of null. */
  statusRedenOp: string | null
  statusRedenDoorId: string | null
  uitvoerOpmerking: string | null
  kilometerstand: number | null
  /** Decimal(9,6) als string of null. */
  uitvoerLat: string | null
  uitvoerLng: string | null
}

function tekstOfNull(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null
}

/**
 * Leid uit de `oudeWaarde` van de laatste UITGEVOERD-audit af naar welke status
 * (en reden/uitvoer-velden) een ongedaan gemaakte gereedmelding terugkeert.
 *
 * Oudere audits (van vóór batch-62) kennen alleen `{ status }`; dan zijn de
 * uitvoer-velden leeg. Onbekend of onbruikbaar -> terugval: Gepland als er een
 * medewerker én datum is, anders Open. Een On hold / Gepland zonder planning
 * bestaat niet, dus dan ook Open.
 */
export function vorigeToestandNaOngedaan(
  oudeWaarde: unknown,
  planning: { heeftUitvoerder: boolean; heeftGeplandeDatum: boolean },
): VorigeTaakToestand {
  const obj =
    oudeWaarde && typeof oudeWaarde === 'object' && !Array.isArray(oudeWaarde)
      ? (oudeWaarde as Record<string, unknown>)
      : {}
  const heeftPlanning = planning.heeftUitvoerder && planning.heeftGeplandeDatum
  const terugval: OperationeleTaakStatus = heeftPlanning ? 'GEPLAND' : 'BACKLOG'

  const kandidaat = typeof obj.status === 'string' ? (obj.status as OperationeleTaakStatus) : null
  let status: OperationeleTaakStatus =
    kandidaat && TERUGKEER_STATUSSEN.has(kandidaat) ? kandidaat : terugval
  if ((status === 'GEPLAND' || status === 'ON_HOLD') && !heeftPlanning) status = 'BACKLOG'

  const houdtReden = status === 'ON_HOLD' || status === 'HERPLANNEN'
  const km = obj.kilometerstand
  return {
    status,
    statusReden: houdtReden ? tekstOfNull(obj.statusReden) : null,
    statusRedenOp: houdtReden ? tekstOfNull(obj.statusRedenOp) : null,
    statusRedenDoorId: houdtReden ? tekstOfNull(obj.statusRedenDoorId) : null,
    uitvoerOpmerking: tekstOfNull(obj.uitvoerOpmerking),
    kilometerstand: typeof km === 'number' && Number.isInteger(km) ? km : null,
    uitvoerLat: tekstOfNull(obj.uitvoerLat),
    uitvoerLng: tekstOfNull(obj.uitvoerLng),
  }
}

// ─── Teksten (zonder jargon, zonder em-dash) ─────────────────────────────────

/**
 * Bevestigingsvraag bij een open checklist ("3 punten nog niet afgevinkt -
 * toch gereedmelden?"). Geen blokkade: null betekent "niets te vragen".
 */
export function openChecklistVraag(aantalOpen: number | null | undefined): string | null {
  const n = aantalOpen ?? 0
  if (n <= 0) return null
  return n === 1
    ? '1 punt nog niet afgevinkt - toch gereedmelden?'
    : `${n} punten nog niet afgevinkt - toch gereedmelden?`
}

/** Wat er na het gereedmelden gebeurt, zoals de gebruiker het te zien krijgt. */
export function gereedgemeldBericht(aanvragerGeinformeerd: boolean): string {
  return aanvragerGeinformeerd ? 'Gereed gemeld. De aanvrager krijgt bericht.' : 'Gereed gemeld.'
}

/** Melding voor de tweede van twee gelijktijdige gereedmeldingen. */
export function alGereedgemeldMelding(doorNaam: string | null | undefined): string {
  const naam = doorNaam?.trim()
  return naam ? `Deze taak is al gereedgemeld door ${naam}.` : 'Deze taak is al gereedgemeld.'
}

// ─── Deadline op de kaart ─────────────────────────────────────────────────────

export type DeadlineToestand = 'TE_LAAT' | 'VANDAAG' | 'OP_TIJD'

/**
 * Kleur-toestand van een deadline ('yyyy-mm-dd', NL-kalenderdag) t.o.v.
 * `vandaag` ('yyyy-mm-dd'). Zonder deadline, of voor een afgesloten taak: null
 * (geen kleur, want er is niets meer te halen).
 */
export function deadlineToestand(
  deadline: string | null | undefined,
  vandaag: string,
  afgesloten = false,
): DeadlineToestand | null {
  if (!deadline || afgesloten) return null
  if (deadline < vandaag) return 'TE_LAAT'
  if (deadline === vandaag) return 'VANDAAG'
  return 'OP_TIJD'
}

/**
 * Standaard-deadline uit een sjabloon: `vandaag` ('yyyy-mm-dd') plus `dagen`
 * kalenderdagen. Rekent op UTC-midnight van de NL-kalenderdag (DST-veilig).
 * Geen of een ongeldig aantal dagen -> null (geen deadline invullen).
 */
export function deadlineOverDagen(vandaag: string, dagen: number | null | undefined): string | null {
  if (dagen == null || !Number.isInteger(dagen) || dagen < 0) return null
  const d = parseDagDatum(vandaag)
  d.setUTCDate(d.getUTCDate() + dagen)
  return formatDagDatum(d)
}

// ─── Taken van een workflow (review I1) ───────────────────────────────────────

/**
 * Bron-modules waarvan een automatische workflow de taak zelf afsluit, of op de
 * status van de taak verder loopt. Zo'n taak gaat niet via "Toch niet gereed" of
 * Heropenen terug open: de bron blijft dan afgehandeld en de taak wordt een wees.
 * Heropenen hoort in die module zelf. Sleutel = `OperationeleTaak.bronModule` in
 * kleine letters; waarde = leesbare naam voor de melding.
 *
 * - `offboarding`: de offboarding-workflow sluit stap-taken zelf (reconciliatie,
 *   salarisverwerking) en leest hun status (services/offboarding-workflow.ts).
 * - `onboarding`: de onboarding-startworkflow sluit een stap-taak zodra de stap
 *   klaar is (services/onboarding-startworkflow.ts).
 * - `bruikleen-trigger`: de bruikleen-bewaking sluit de taak zodra de
 *   overeenkomst er is (lib/cron/bruikleen-openstaand-bewaking.ts).
 * - `assets`: het opzegbevestiging-taakje sluit bij een gekoppeld document
 *   (services/abonnement/opzegtaak.ts).
 *
 * Bewust NIET in deze lijst: `voertuig-onderhoud-reminder` (batch-62 garage-voertuig).
 * De onderhoudsreminder sluit zijn taak automatisch zodra de afspraak gepland of het
 * werk uitgevoerd is; die automatische afsluiting schrijft (zoals de afsluiters
 * hierboven) een audit zonder vorige toestand, zodat het undo-endpoint hem weigert.
 * Een reminder-taak die een MENS gereedmeldde (bv. een misveeg) moet wél binnen 24
 * uur terug kunnen: de reminder maakt voor dezelfde cyclus geen nieuwe taak aan.
 */
const WORKFLOW_TAAK_BRON_LABELS: ReadonlyMap<string, string> = new Map([
  ['offboarding', 'de offboarding'],
  ['onboarding', 'de onboarding'],
  ['bruikleen-trigger', 'de bruikleenovereenkomst'],
  ['assets', 'het abonnement'],
  // batch-66 contract-mijlpalen (FO-496): een taak van een contractmijlpaal sluit
  // samen met de mijlpaal; heropenen zou de mijlpaal afgerond laten met een open
  // taak ernaast (bronModule 'CONTRACT', hier in kleine letters).
  ['contract', 'het contractdossier'],
  // batch-70 voertuig-meldingen (FO-527 §C7): de handmatige Cartracker-taak; gereed
  // zet de melding op "handmatig gedaan". Heropenen zou de melding afgevinkt laten
  // terwijl de taak weer open staat: heropenen hoort op Voertuigen > Mutatiemeldingen.
  ['voertuig-meldingen', 'de voertuigmelding'],
  // batch-71 workflow-sjablonen (FO-533): een taak van een stappenplan sluit samen met zijn
  // stap. Heropenen zou de stap afgerond laten met een open taak ernaast: dat doet de motor
  // zelf als de controle weer faalt, of de eigenaar met "Opnieuw".
  ['workflow', 'een stappenplan'],
])

/** De workflow-bronnen (kleine letters), zie `WORKFLOW_TAAK_BRON_LABELS`. */
export const WORKFLOW_TAAK_BRONNEN: ReadonlySet<string> = new Set(WORKFLOW_TAAK_BRON_LABELS.keys())

function bronSleutel(bronModule: string | null | undefined): string {
  return (bronModule ?? '').trim().toLowerCase()
}

/** Hoort deze taak bij een workflow die hem zelf afsluit (hoofdletter-ongevoelig)? */
export function isWorkflowTaakBron(bronModule: string | null | undefined): boolean {
  return WORKFLOW_TAAK_BRONNEN.has(bronSleutel(bronModule))
}

/** Leesbare naam van de bron ("de offboarding"), of "een andere module". */
export function workflowTaakBronLabel(bronModule: string | null | undefined): string {
  return WORKFLOW_TAAK_BRON_LABELS.get(bronSleutel(bronModule)) ?? 'een andere module'
}

/** Melding als een workflow-taak hier niet terug open kan. */
export function workflowTaakMelding(bronModule: string | null | undefined): string {
  return `Deze taak hoort bij ${workflowTaakBronLabel(bronModule)} en wordt daar afgehandeld.`
}
